import { useState, useEffect, memo, useContext, useRef } from 'react';
// store
import { useSelector, useDispatch, SET_RANGES_RECENTLY_SENSOR, SET_RANGES_LOCATION_SENSORS, SET_RANGES_LOCATION_PREDICTIONS } from 'store';
// hooks
import useResponsive from 'hooks/useResponsive';
// sections
import ContentDevices from './ContentDevices';
import ContentSensors from './ContentSensors';
import RecentlySensors from './RecentlySensors';
import MeasurementChart from './MeasurementChart';
import PredictionsChart from './PredictionsChart';
// components
import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import { Label, TextFields, Message as FormMessage } from '../../../components/hook-form';
// @event-source-polyfill
import { EventSourcePolyfill } from 'event-source-polyfill';
// @devextreme
import ArrayStore from 'devextreme/data/array_store';
import { Popover } from 'devextreme-react/popover';
// assets
import { ReactComponent as Water } from 'assets/real-time/water.svg';
import { ReactComponent as Location } from 'assets/real-time/location.svg';
import { ReactComponent as Status } from 'assets/button/status.svg';
import { ReactComponent as Line } from 'assets/real-time/line.svg';
import { ReactComponent as Settings } from 'assets/real-time/settings.svg';
// styles
import { ThemeContext } from 'styled-components';
import { Container, RealTimeTopWrapper, IconButton, RealTimeTabs, StatusPopover, StatusIcon, LinePopover, RangesPopover, TextSwitch, PrimaryButton, Legend } from 'styles';
// utils
import { timezones } from 'utils/format';
// apis
import { getRealTime, checkReissue } from 'apis';

// ----------------------------------------------------------------------

const colors = ['blue', 'green', 'yellow', 'purple', 'pink', 'error'];

const rsrp = (value: number) => {
  if ( value >= -80 ) return 'excellent';
  if ( value >= -90 && value < -80 ) return 'good';
  if ( value > -100 && value < -90 ) return 'medium';
  if ( value <= -100 ) return 'weak';
}

const battery = (value: number, alarm: boolean) => {
  if ( value === null ) return 'battery-null';
  if ( value <= 25 ) return `25${alarm ? 'red' : ''}`;
  if ( value > 25 && value <= 50 ) return `50${alarm ? 'red' : ''}`;
  if ( value > 50 && value <= 75 ) return `75${alarm ? 'red' : ''}`;
  if ( value > 75 ) return '100';
}

export default function RealTimePage() {
  const dispatch = useDispatch();
  const theme = useContext(ThemeContext);
  const mobile = useResponsive('down', 'md');

  const token = useSelector((state) => state.token);
  const group = useSelector((state) => state.group);
  const devices = useSelector((state) => state.devices);

  const sse = useRef(null);

  const [ tab, setTab ] = useState('devices');

  const [ data, setData ] = useState(null);
  const [ realTimeData, setRealTimeData ] = useState(null);

  //& 수질 항목 컨텐츠 --- 나중에 정리할 것
  const info = useRef(null);
  const [ visibleRanges, setVisibleRanges ] = useState<{[key: string]: any}>({});
  
  const [ alarmLines, setAlarmLines ] = useState<{[key: string]: any}>({});
  const [ aiAlarmLines, setAiAlarmLines ] = useState<{[key: string]: any}>({});

  const [ status, setStatus ] = useState(null);
  const [ statusVisible, setStatusVisible ] = useState(false);

  const [ visibleLines, setVisibleLines ] = useState<{[key: string]: boolean}>({});
  const [ lines, setLines ] = useState<{[key: string]: any}>({});
  const [ rangesa, setRangesa ] = useState<{[key: string]: any}>({});

  const [ aiVisibleLines, setAiVisibleLines ] = useState<{[key: string]: boolean}>({});
  const [ aiLines, setAiLines ] = useState<{[key: string]: any}>({});
  const [ aiRanges, setAiRanges ] = useState<{[key: string]: any}>({});
  
  useEffect(() => {
    if ( devices?.length ) {
      const devicesObject: {[key: string]: any} = {iotDeviceId: [], sensors: [], predictions: []};
      const name: {[key: string]: any} = {};
      const range: {[key: string]: any} = {};
      const aiName: {[key: string]: any} = {};
      const aiRange: {[key: string]: any} = {};

      devices.forEach((element: any) => {
        devicesObject.iotDeviceId.push(element.iotDeviceId);

        devicesObject[element.iotDeviceId] = {
          name: 'waterTemperature',
          location: element.location,
          unit: group.waterTemperatureUnit,
          powerType: element.powerType,
          number: element.number
        };

        element.waterQualitySensors.forEach((sensor: any) => {
          devicesObject.sensors.push(sensor.name);

          devicesObject[sensor.name] = devicesObject[sensor.name] && devicesObject[sensor.name] < Number(String(element.number) + String(sensor.channel)) ? devicesObject[sensor.name] : Number(String(element.number) + String(sensor.channel));

          devicesObject[element.iotDeviceId] = { ...devicesObject[element.iotDeviceId], [sensor.name]: sensor.waterQualitySensorId };

          devicesObject[sensor.waterQualitySensorId] = {
            name: sensor.name,
            location: element.location,
            alarmType: sensor.alarmType,
            alarmLowerLimit: sensor.alarmLowerLimit,
            alarmUpperLimit: sensor.alarmUpperLimit,
            unit: sensor.unit,
            number: element.number,
            channel: sensor.channel,
            totalNum: Number(String(element.number) + String(sensor.channel))
          };
          name[sensor.name] = false;
          range[sensor.name] = { start: undefined, end: undefined };
        });

        element.waterQualityPredictions.forEach((prediction: any) => {
          if ( prediction.waterQualityPredictionId ) {
            devicesObject.predictions.push(prediction.name);
            
            devicesObject[element.iotDeviceId] = { ...devicesObject[element.iotDeviceId], [prediction.name]: prediction.waterQualityPredictionId };
  
            devicesObject[prediction.waterQualityPredictionId] = {
              name: prediction.name,
              location: element.location,
              number: element.number,
              alarmType: prediction.alarmType,
              alarmLowerLimit: prediction.alarmLowerLimit,
              alarmUpperLimit: prediction.alarmUpperLimit,
              unit: prediction.unit
            };
            aiName[prediction.name] = false;
            aiRange[prediction.name] = { start: undefined, end: undefined };
          }
        });
      });
      
      const sensors = devicesObject.sensors.filter((element: any, index: number) => devicesObject.sensors.indexOf(element) === index);
      let sort: any = [];
      sensors.forEach((element: any) => {
        sort.push({name: element, num: devicesObject[element]});
      });
      sort = sort.sort((a: any, b: any) => a.num - b.num);
      devicesObject.sensors = sort.map((element: any) => element.name);

      devicesObject.predictions = devicesObject.predictions.filter((element: any, index: number) => devicesObject.predictions.indexOf(element) === index);
      
      setVisibleLines(name);
      setLines(name);
      setRangesa(range);
      setVisibleRanges({ ...name, ...aiName });
      
      setAiVisibleLines(aiName);
      setAiLines(aiName);
      setAiRanges(aiRange);

      info.current = devicesObject;
    };
  }, [devices]);

  const [ recentlyVisibleLines, setRecentlyVisibleLines ] = useState(false);
  const [ recentlyLines, setRecentlyLines ] = useState(null);
  
  const ranges = useSelector((state) => state.rangesRecentlySensor);
  const [ visibleRecentlyRanges, setVisibleRecentlyRanges ] = useState(false);
  const [ range, setRange ] = useState(ranges);
  const [ message, setMessage ] = useState(false);
    
  useEffect(() => {
    setRange(ranges);
  }, [ranges]);

  const handleRanges = (name: string) => {
    if ( range[name].manual && ( range[name].startValue > range[name].endValue )) {
      return setMessage(true);
    } else {
      dispatch( SET_RANGES_RECENTLY_SENSOR({ ...ranges, [name]: range[name] }));
      setVisibleRecentlyRanges(false);
      setMessage(false);
    }
  };
  
  const [ recentlySensors, setRecentlySensors ] = useState(null);

  const [ legend, setLegend ] = useState(null);
  const [ datasets, setDatasets ] = useState(null);

  const closeRecentlySensors = () => {
    setRecentlySensors(null);
    setDatasets(null);
    setLegend(null);
  };

  useEffect(() => {
    if ( recentlySensors ) {
      const recently = deepCopy(recentlySensors);
      const legendSet: {[key: string]: any} = {};
      recently.id.forEach((id: string, index: number) => {
        legendSet[id] = true;
      });
      setLegend(legendSet);
    }
  }, [recentlySensors]);

  useEffect(() => {
    if ( legend ) {
      const recently = deepCopy(recentlySensors);
      const legendSet = deepCopy(legend);
      const legendArray: any[] = [];
      const colorSet: {[key: string]: any} = {};
      const dataStore: any[] = [];
      const dataArray: any[] = [];
      recently.id.forEach((id: string, index: number) => {
        colorSet[id] = `${theme.colors[colors[index]]}`;
        if ( legendSet[id] ) {
          legendArray.push(id);
          if ( recently.dataset[id] ) {
            dataStore.push(...recently.dataset[id]);
            recently.dataset[id].forEach((element: any) => {
              dataArray.push(element[id]);
            });
          }
        }
      });
      recently.color = colorSet;
      recently.legend = legendArray;
      recently.dataArr = dataArray;
      recently.dataStore = new ArrayStore({ key: 'waterQualities', data: dataStore });
      setDatasets(recently);
    }
  }, [legend]);

  //& 여기까지 수질 항목 컨텐츠 --- 나중에 정리할 것

  useEffect(() => {
    // 토큰 자동 갱신
    if ( token ) {
      const interval = setInterval(checkReissue, 300000); // 5분
      return () => clearInterval(interval);
    }
  }, [token]);

  useEffect(() => {
    if ( group.id ) {
      ( async () =>
        await getRealTime(setData, group.id)
      )();
    }
  }, [group.id]);

  const [ recentlyDevices, setRecentlyDevices ] = useState(null);
  const closeRecentlyDevices = () => setRecentlyDevices(null);

  const [ infomation, setInfomation ] = useState(null);
  const [ deviceinfo, setDeviceinfo ] = useState({ iotDeviceId: null, location: '', waterQualitySensors: [], waterQualityPredictions: [] });
  const [ legendLocation, setLegendLocation ] = useState({ sensors: null, predictions: null });

  const [ devicesDatasets, setDevicesDatasets ] = useState<{[key: string]: any}>({ waterQualities: {}, waterQualityPredictions: {} });

  const [ sensorsData, setSensorsData ] = useState(null);
  const [ predictionsData, setPredictionsData ] = useState(null);

  const rangesLocationSensors = useSelector((state) => state.rangesLocationSensors);
  const rangesLocationPredictions = useSelector((state) => state.rangesLocationPredictions);
  
  const [ visibleRangesLocation, setVisibleRangesLocation ] = useState({ sensors: false, predictions: false });
  const [ rangeLocationSensors, setRangeLocationSensors ] = useState(ranges);
  const [ rangeLocationPredictions, setRangeLocationPredictions ] = useState(ranges);
  const [ messageLocation, setMessageLocation ] = useState(false);
  
  const [ visibleLinesLocation, setVisibleLinesLocation ] = useState({ sensors: false, predictions: false });
  const [ linesLocation, setLinesLocation ] = useState({ sensors: null, predictions: null });
  
  useEffect(() => {
    setRangeLocationSensors(rangesLocationSensors);
  }, [rangesLocationSensors]);
  useEffect(() => {
    setRangeLocationPredictions(rangesLocationPredictions);
  }, [rangesLocationPredictions]);

  const handleRangesLocation = () => {
    const targets: {[key: string]: any} = {};
    deviceinfo.waterQualitySensors.forEach((sensor) => {
      targets[sensor.waterQualitySensorId] = rangeLocationSensors[sensor.waterQualitySensorId];
    });
    targets.waterTemperature = rangeLocationSensors.waterTemperature;
    const faild = Object.values(targets).find((item: any) => { return item.manual && ( item.startValue > item.endValue ) });
    if ( faild ) {
      return setMessageLocation(true);
    } else {
      dispatch( SET_RANGES_LOCATION_SENSORS({ ...rangeLocationSensors, ...targets }));
      setVisibleRangesLocation({ ...visibleRangesLocation, sensors: false });
      setMessageLocation(false);
    }
  };

  const handleRangesLocationPredictions = () => {
    const targets: {[key: string]: any} = {};
    deviceinfo.waterQualityPredictions.forEach((prediction) => {
      targets[prediction.waterQualityPredictionId] = rangeLocationPredictions[prediction.waterQualityPredictionId];
    });
    const faild = Object.values(targets).find((item: any) => { return item.manual && ( item.startValue > item.endValue ) });
    if ( faild ) {
      return setMessageLocation(true);
    } else {
      dispatch( SET_RANGES_LOCATION_PREDICTIONS({ ...rangeLocationPredictions, ...targets }));
      setVisibleRangesLocation({ ...visibleRangesLocation, predictions: false });
      setMessageLocation(false);
    }
  };

  useEffect(() => {
    if ( recentlyDevices ) {
      const recently = deepCopy(recentlyDevices);
      let identical = { ...devices.find((item: any) => item.iotDeviceId === recently.deviceId) };
      let waterQualitySensors = [ ...identical.waterQualitySensors ];
      let waterQualityPredictions = [ ...identical.waterQualityPredictions ];
      waterQualitySensors = waterQualitySensors.sort((a: any, b: any) => {
        return a.channel - b.channel;
      });
      waterQualityPredictions = waterQualityPredictions.sort((a: any, b: any) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      });
      identical.waterQualitySensors = waterQualitySensors;
      identical.waterQualityPredictions = waterQualityPredictions;
      setDeviceinfo(identical);
      const sensors: {[key: string]: any} = {};
      const predictions: {[key: string]: any} = {};
      const info: {[key: string]: any} = {};
      identical.waterQualitySensors.forEach((values: any, index: number) => {
        sensors[values.waterQualitySensorId] = true;
        info[values.name] = {
          ...values,
          color: colors[index]
        };
        info[values.waterQualitySensorId] = {
          ...values,
          color: colors[index]
        };
      });
      identical.waterQualityPredictions.forEach((values: any, index: number) => {
        predictions[values.waterQualityPredictionId] = true;
        info[values.waterQualityPredictionId] = {
          ...values,
          color: colors[index]
        };
        info[values.name] = {
          ...values,
          color: colors[index]
        };
      });
      info.waterTemperature = {
        name: group.waterTemperature,
        unit: group.waterTemperatureUnit,
        color: colors[identical.waterQualitySensors.length]
      };
      info[group.waterTemperature] = {
        name: group.waterTemperature,
        unit: group.waterTemperatureUnit,
        color: colors[identical.waterQualitySensors.length]
      }
      sensors.waterTemperature = true;
      setInfomation(info);
      setLegendLocation({ sensors, predictions });

      let waterQualitiesData: {[key: string]: any} = {};
      let waterQualityPredictionsData: {[key: string]: any} = {};
      recently.waterQualities.forEach((values: any) => {
        const sets = values.detailData.map((value: any) => {
          return {
            ...value,
            [values.waterQualitySensorId]:
            value.measuredValue,
            acquisitionAt: timezones(value.acquisitionAt, 'YYYY-MM-DD HH:mm:ss')
          };
        });
        waterQualitiesData[values.waterQualitySensorId] = sets;
      });
      const sets = recently.waterTemperatures.map((value: any) => {
        return {
          ...value,
          acquisitionAt: timezones(value.acquisitionAt, 'YYYY-MM-DD HH:mm:ss')
        };
      });
      waterQualitiesData.waterTemperature = sets;
      recently.waterQualityPredictions.forEach((values: any) => {
        const sets = values.detailData.map((value: any) => {
          return {
            ...value,
            [values.waterQualityPredictionId]: value.predictionValue,
            acquisitionAt: timezones(value.acquisitionAt, 'YYYY-MM-DD HH:mm:ss')
          };
        });
        waterQualityPredictionsData[values.waterQualityPredictionId] = sets;
      });
      setDevicesDatasets({ waterQualities: waterQualitiesData, waterQualityPredictions: waterQualityPredictionsData });
    }
  }, [recentlyDevices]);
  
  useEffect(() => {
    if ( legendLocation.sensors && devicesDatasets && infomation ) {
      const recently: {[key: string]: any} = { datasets: deepCopy(devicesDatasets.waterQualities) };
      const legendSet = deepCopy(legendLocation.sensors);
      const legendArray: any[] = [];
      const ids = Object.keys(recently.datasets);
      const colorSet: {[key: string]: any} = {};
      const dataStore: any[] = [];
      const dataArray: {[key: string]: any} = {};
      const name: {[key: string]: any} = {};
      const unit: {[key: string]: any} = {};
      const alarm: {[key: string]: any} = {};
      ids.forEach((id: string, index: number) => {
        dataArray[id] = [];
        colorSet[id] = `${theme.colors[colors[index]]}`;
        name[id] = infomation[id].name;
        unit[infomation[id].name] = infomation[id].unit;
        alarm[id] = {
          alarmLowerLimit: infomation[id].alarmLowerLimit,
          alarmUpperLimit: infomation[id].alarmUpperLimit
        };
        if ( legendSet[id] ) {
          legendArray.push(id);
          if ( recently.datasets[id] ) {
            dataStore.push(...recently.datasets[id]);
            recently.datasets[id].forEach((element: any) => {
              dataArray[id] = [ ...dataArray[id], element[id] ];
            });
          }
        }
      });
      recently.id = ids;
      recently.name = name;
      recently.unit = unit;
      recently.alarm = alarm;
      recently.color = colorSet;
      recently.legend = legendArray;
      recently.dataArr = dataArray;
      recently.dataStore = new ArrayStore({ key: 'waterQualities', data: dataStore });
      setSensorsData(recently);
    }
  }, [legendLocation.sensors]);

  useEffect(() => {
    if ( legendLocation.predictions && devicesDatasets ) {
      const recently: {[key: string]: any} = { datasets: deepCopy(devicesDatasets.waterQualityPredictions) };
      const legendSet = deepCopy(legendLocation.predictions);
      const legendArray: any[] = [];
      const ids = Object.keys(recently.datasets);
      const colorSet: {[key: string]: any} = {};
      const dataStore: any[] = [];
      const dataArray: {[key: string]: any} = {};
      const name: {[key: string]: any} = {};
      const unit: {[key: string]: any} = {};
      const alarm: {[key: string]: any} = {};
      ids.forEach((id: string, index: number) => {
        dataArray[id] = [];
        colorSet[id] = `${theme.colors[colors[index]]}`;
        name[id] = infomation[id].name;
        unit[infomation[id].name] = infomation[id].unit;
        alarm[id] = {
          alarmLowerLimit: infomation[id].alarmLowerLimit,
          alarmUpperLimit: infomation[id].alarmUpperLimit
        };
        if ( legendSet[id] ) {
          legendArray.push(id);
          if ( recently.datasets[id] ) {
            dataStore.push(...recently.datasets[id]);
            recently.datasets[id].forEach((element: any) => {
              dataArray[id] = [ ...dataArray[id], element[id] ];
            });
          }
        }
      });
      recently.id = ids;
      recently.name = name;
      recently.unit = unit;
      recently.alarm = alarm;
      recently.color = colorSet;
      recently.legend = legendArray;
      recently.dataArr = dataArray;
      recently.dataStore = new ArrayStore({ key: 'waterQualityPredictions', data: dataStore });
      setPredictionsData(recently);
    }
  }, [legendLocation.predictions]);
  

  useEffect(() => {
    if ( data && realTimeData ) {
      const state = deepCopy(data);
      const current = Object.values(deepCopy(realTimeData));
      current.forEach((element: any) => {
        const index = state.findIndex((item: any) => item.iotDeviceId === element.iotDeviceId);
        if ( index > -1 ) {
          const merge = { ...state[index], ...element };
          let waterQualities: any[] = [];
          let predictions: any[] = [];
          element.waterQualities.forEach((value: any) => {
            const sensor = state[index].waterQualities.find((item: any) => item.waterQualitySensorId === value.waterQualitySensorId);
            const values = { ...sensor, ...value };
            waterQualities.push(values);
          });
          if ( element.waterQualityPredictions.length > 0 ) {
            element.waterQualityPredictions.forEach((value: any) => {
              const prediction = state[index].waterQualityPredictions.find((item: any) => item.waterQualityPredictionId === value.waterQualityPredictionId);
              const values = { ...prediction, ...value, measuredValue: value.predictionValue };
              predictions.push(values);
            });
          } else {
            const prediction = devices.find((item: any) => item.iotDeviceId === element.iotDeviceId);
            prediction.waterQualityPredictions.forEach((value: any) => {
              predictions.push({
                ...value,
                measuredValue: null
              });
            });
          }
          merge.waterQualities = waterQualities;
          merge.waterQualityPredictions = predictions;
          state.splice(index, 1, merge);
        }
      });
      setData(state);
    }
  }, [realTimeData]);
  
  useEffect(() => {
    if ( info.current && data ) {
      const infoCurrent = info.current;
      const statusObj: {[key: string]: any} = {};
      data.forEach((element: any) => {
        if ( infoCurrent.iotDeviceId.includes(element.iotDeviceId) ) {
          statusObj[element.iotDeviceId] = {
            iotDeviceId: element.iotDeviceId,
            lowBattery: element.lowBattery,
            battery: infoCurrent[element.iotDeviceId].powerType === 'COMMERCIAL' ? 'plug' : element.batteryLevel,
            rsrp: element.noSignal || element.rsrp === null ? 'noSignal' : element.rsrp
          };
        }
      });
      setStatus(statusObj);
    }
  }, [info.current, data]);

  const handleRealTimeData = (data: any) => {
    setRealTimeData((state: any) => ({ ...state, [data.iotDeviceId]: data }));
  };

  const handleRealTimeSignal = (data: any) => {
    const state = deepCopy(data);
    const current = deepCopy(data);
    const index = state.findIndex((item: any) => item.iotDeviceId === current.iotDeviceId);
    if ( index > -1 ) {
      state.forEach((element: any) => {
        const merge = deepCopy(element);
        let waterQualities: any[] = [];
        let predictions: any[] = [];
        element.waterQualities.forEach((value: any) => {
          waterQualities.push({
            ...value,
            measuredValue: null
          });
        });
        element.waterQualityPredictions.forEach((value: any) => {
          predictions.push({
            ...value,
            predictionValue: null
          });
        });
        merge.waterQualities = waterQualities;
        merge.waterQualityPredictions = predictions;
        state.splice(index, 1, { ...merge, waterTemperature: null });
      });
      setData(state);
    }
  };
  
  useEffect(() => {
    if ( token && group.id ) {
      sse.current = new EventSourcePolyfill(`/sse/notification/subscribe/iot-groups/${group.id}`, {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true,
        heartbeatTimeout: 600000 // 10분
      });

      if ( sse.current ) {
        sse.current.addEventListener('iot-device-data', (event: MessageEvent) => {
          handleRealTimeData(JSON.parse(event.data));
        });
  
        sse.current.addEventListener('iot-device-no-signal', (event: MessageEvent) => {
          handleRealTimeSignal(JSON.parse(event.data));
        });
      }
    }

    return () => {
      if ( sse.current ) sse.current.close();
    }
  }, [token, group.id, devices]);

  const deepCopy = (object: any) => {
    if (object === null || typeof object !== 'object' ) {
      return object;
    } 
    const copy: any = Array.isArray(object) ? [] : {};
    for ( let key of Object.keys(object) ) {
      copy[key] = deepCopy(object[key]);
    }
    return copy;
  };

  return (
    <Container>
      <RealTimeTopWrapper>
        <Breadcrumbs heading='실시간 현황' />

        <div>
          <IconButton type='button' id='status' onClick={() => setStatusVisible(!statusVisible)} disabled={group.id ? false : true}>
            <Status />
            IoT 장비 현황
          </IconButton>
          <Popover
            target='#status'
            visible={statusVisible}
            hideOnOutsideClick={true}
            onHiding={() => setStatusVisible(false)}
          >
            <StatusPopover>
              { status ? (
                <>
                  <div>
                    { Object.keys(status).map((item: any, index) => (
                      <p key={index}>{ info.current[item]?.location }</p>
                    ))}
                  </div>
                  <div>
                  { Object.keys(status).map((item: any, index) => (
                    <div key={index}>
                      <StatusIcon $icon={status[item].battery === 'plug' ? 'plug' : battery(status[item].battery, status[item].lowBattery)}>
                        <span>{ status[item].battery === 'plug' ? '상시 전원' : status[item].battery === null ? '-' : status[item].battery + '%' }</span>
                      </StatusIcon>
                      <StatusIcon $icon={status[item].rsrp === 'noSignal' ? 'noSignal' : rsrp(status[item].rsrp)}>
                        <span>{ status[item].rsrp === 'noSignal' ? '신호 없음' : status[item].rsrp }</span>
                      </StatusIcon>
                    </div>
                  ))}
                  </div>
                </>
              ) : (
                <p style={{'borderBottom': 'none', 'padding': '0', 'lineHeight': '25px'}}>등록된 장비가 없습니다.</p>
              )}
            </StatusPopover>
          </Popover>
        </div>
      </RealTimeTopWrapper>

      <RealTimeTabs>
        <button type='button' onClick={() => setTab('devices')} className={ tab === 'devices' ? 'active' : ''}>
          <Location style={{ 'width': '24px', 'height': '24px' }} />
          측정 위치
        </button>
        <button
          type='button'
          onClick={() => {
            setTab('water');
          }}
          className={ tab === 'water' ? 'active' : ''}
        >
          <Water style={{ 'width': '26px', 'height': '26px' }} />
          수질 항목
        </button>
      </RealTimeTabs>


      { data && devices &&
        <div>
          { tab === 'devices' ? (
            <ContentDevices
              data={data}
              group={group}
              setRecently={setRecentlyDevices}
            />
          ): (
            info.current &&
            <ContentSensors
              info={info}
              setRecently={setRecentlySensors}
              alarmLines={alarmLines}
              setAlarmLines={setAlarmLines}
              aiAlarmLines={aiAlarmLines}
              setAiAlarmLines={setAiAlarmLines}
              data={data}
              group={group}
              visibleRanges={visibleRanges}
              setVisibleRanges={setVisibleRanges}
              visibleLines={visibleLines}
              setVisibleLines={setVisibleLines}
              lines={lines}
              setLines={setLines}
              ranges={rangesa}
              setRanges={setRangesa}
              aiVisibleLines={aiVisibleLines}
              setAiVisibleLines={setAiVisibleLines}
              aiLines={aiLines}
              setAiLines={setAiLines}
              aiRanges={aiRanges}
              setAiRanges={setAiRanges}
            />
          )}
        </div>
      }

      { recentlyDevices &&
        <Dialog
          title={deviceinfo.location}
          handleClose={closeRecentlyDevices}
          chart
        >
          <div style={{ 'width': mobile ? 'calc(100vw - 60px)' : '1000px'}}>
            <div style={{'display': 'flex', 'alignItems': 'center', 'justifyContent': 'space-between', 'marginBottom': '10px'}}>
              <p>센서 측정 값</p>
              <div style={{'display': 'flex', 'gap': '5px'}}>
                <button id='sensorLinesLocation' onClick={() => setVisibleLinesLocation({ ...visibleLinesLocation, sensors: !visibleLinesLocation.sensors })}><Line /></button>
                <button id='sensorRangesLocation' onClick={() => setVisibleRangesLocation({ ...visibleRangesLocation, sensors: !visibleRangesLocation.sensors })}><Settings /></button>
              </div>
            </div>
            <Popover
              target='#sensorLinesLocation'
              visible={visibleLinesLocation.sensors}
              hideOnOutsideClick={true}
              onHiding={() => setVisibleLinesLocation({ ...visibleLinesLocation, sensors: false })}
            >
              <LinePopover>
                <p>알람 기준선 설정</p>
                { deviceinfo.waterQualitySensors.length > 0 && deviceinfo.waterQualitySensors.filter((sensor) => { return infomation[sensor.waterQualitySensorId].alarmType !== 'OFF' }).length > 0 ?
                  deviceinfo.waterQualitySensors.map((sensor: any, index: number) => (
                    infomation[sensor.waterQualitySensorId].alarmType !== 'OFF' &&
                      <label key={index}>
                        <input
                          type='checkbox'
                          id={`${sensor.waterQualitySensorId}Location`}
                          checked={linesLocation.sensors === sensor.name}
                          onChange={(event) => setLinesLocation({ ...linesLocation, sensors: event.target.checked ? sensor.name : null })}
                        />
                        <label htmlFor={`${sensor.waterQualitySensorId}Location`} />
                        { sensor.name }
                      </label>
                    )
                  ) : (
                    <label>설정 없음</label>
                  )
                }
              </LinePopover>
            </Popover>
            <Popover
              target='#sensorRangesLocation'
              visible={visibleRangesLocation.sensors}
              hideOnOutsideClick={true}
              onHiding={() => {
                setVisibleRangesLocation({ ...visibleRangesLocation, sensors: false });
                setRangeLocationSensors(rangesLocationSensors);
                setMessageLocation(false);
              }}
            >
              <RangesPopover>
                <p>데이터 범위 설정</p>
                <div>
                  { deviceinfo.waterQualitySensors.length > 0 && deviceinfo.waterQualitySensors.map((sensor: any, index: number) => (
                    rangeLocationSensors &&
                    <div key={index}>
                      <Label>{ sensor.name }</Label>
                      
                      <div>
                        <TextSwitch $text={ rangeLocationSensors[sensor.waterQualitySensorId].manual ? '수동' : '자동' }>
                          <label>
                            <input
                              type='checkbox'
                              checked={rangeLocationSensors[sensor.waterQualitySensorId].manual}
                              onChange={(event) => setRangeLocationSensors({
                                ...rangeLocationSensors,
                                [sensor.waterQualitySensorId]: {
                                  ...rangeLocationSensors[sensor.waterQualitySensorId],
                                  manual: event.target.checked
                                }
                              })}
                            />
                          </label>
                        </TextSwitch>
                        <TextFields
                          type='number'
                          disabled={ rangeLocationSensors[sensor.waterQualitySensorId].manual ? false : true }
                          value={ rangeLocationSensors[sensor.waterQualitySensorId].startValue }
                          onChange={(event) => setRangeLocationSensors({
                            ...rangeLocationSensors,
                            [sensor.waterQualitySensorId]: {
                              ...rangeLocationSensors[sensor.waterQualitySensorId],
                              startValue: Number(event.target.value)
                            }
                          })}
                        />
                        <p>~</p>
                        <TextFields
                          type='number'
                          disabled={ rangeLocationSensors[sensor.waterQualitySensorId].manual ? false : true }
                          value={ rangeLocationSensors[sensor.waterQualitySensorId].endValue }
                          onChange={(event) => setRangeLocationSensors({
                            ...rangeLocationSensors,
                            [sensor.waterQualitySensorId]: {
                              ...rangeLocationSensors[sensor.waterQualitySensorId],
                              endValue: Number(event.target.value)
                            }
                          })}
                        />
                      </div>
                    </div>
                  ))}
                  { rangeLocationSensors &&
                    <div>
                      <Label>{ group.waterTemperature }</Label>
                      
                      <div>
                        <TextSwitch $text={ rangeLocationSensors.waterTemperature.manual ? '수동' : '자동' }>
                          <label>
                            <input
                              type='checkbox'
                              checked={rangeLocationSensors.waterTemperature.manual}
                              onChange={(event) => setRangeLocationSensors({
                                ...rangeLocationSensors,
                                waterTemperature: {
                                  ...rangeLocationSensors.waterTemperature,
                                  manual: event.target.checked
                                }
                              })}
                            />
                          </label>
                        </TextSwitch>
                        <TextFields
                          type='number'
                          disabled={ rangeLocationSensors.waterTemperature.manual ? false : true }
                          value={ rangeLocationSensors.waterTemperature.startValue }
                          onChange={(event) => setRangeLocationSensors({
                            ...rangeLocationSensors,
                            waterTemperature: {
                              ...rangeLocationSensors.waterTemperature,
                              startValue: Number(event.target.value)
                            }
                          })}
                        />
                        <p>~</p>
                        <TextFields
                          type='number'
                          disabled={ rangeLocationSensors.waterTemperature.manual ? false : true }
                          value={ rangeLocationSensors.waterTemperature.endValue }
                          onChange={(event) => setRangeLocationSensors({
                            ...rangeLocationSensors,
                            waterTemperature: {
                              ...rangeLocationSensors.waterTemperature,
                              endValue: Number(event.target.value)
                            }
                          })}
                        />
                      </div>
                    </div>
                  }
                  { messageLocation &&
                    <FormMessage>입력하신 범위를 다시 확인해 주세요.</FormMessage>
                  }
                  <PrimaryButton type='button' onClick={() => handleRangesLocation()}>저장</PrimaryButton>
                </div>
              </RangesPopover>
            </Popover>
          </div>

          { deviceinfo && legendLocation.sensors && infomation &&
            <Legend>
              { deviceinfo.waterQualitySensors.map((sensor: any) => (
                <button
                  key={sensor.waterQualitySensorId}
                  type='button'
                  style={{ 'opacity': legendLocation.sensors[sensor.waterQualitySensorId] ? '1' : '0.5' }}
                  onClick={() => {
                    if ( legendLocation.sensors[sensor.waterQualitySensorId] && Object.values(legendLocation.sensors).filter((bool) => {return bool === true}).length < 2 ) return;
                    setLegendLocation({ ...legendLocation, sensors: { ...legendLocation.sensors, [sensor.waterQualitySensorId]: !legendLocation.sensors[sensor.waterQualitySensorId] }});
                  }}
                >
                  <div style={{'backgroundColor': `${theme.colors[infomation[sensor.waterQualitySensorId].color]}`, 'width': '12px', 'height': '12px', 'borderRadius': '2px'}} />
                  { sensor.name }
                </button>
              ))}
              <button
                type='button'
                style={{ 'opacity': legendLocation.sensors.waterTemperature ? '1' : '0.5' }}
                onClick={() => {
                  if ( legendLocation.sensors.waterTemperature && Object.values(legendLocation.sensors).filter((bool) => {return bool === true}).length < 2 ) return;
                  setLegendLocation({ ...legendLocation, sensors: { ...legendLocation.sensors, waterTemperature: !legendLocation.sensors.waterTemperature }});
                }}
              >
                <div style={{'backgroundColor': `${theme.colors[infomation.waterTemperature.color]}`, 'width': '12px', 'height': '12px', 'borderRadius': '2px'}} />
                { group.waterTemperature }
              </button>
            </Legend>
          }

          { infomation && sensorsData &&
            <div style={{paddingTop: 5}}>
              <MeasurementChart
                infomation={infomation}
                datasets={sensorsData}
                lines={linesLocation.sensors}
              />
            </div>
          }

          <div style={{ 'width': mobile ? 'calc(100vw - 60px)' : '1000px', marginTop: 10}}>
            <div style={{'display': 'flex', 'alignItems': 'center', 'justifyContent': 'space-between', 'marginBottom': '10px'}}>
              <p>AI 예측 값</p>
              <div style={{'display': 'flex', 'gap': '5px'}}>
                <button id='predictionLinesLocation' onClick={() => setVisibleLinesLocation({ ...visibleLinesLocation, predictions: !visibleLinesLocation.predictions })}><Line /></button>
                <button id='predictionRangesLocation' onClick={() => setVisibleRangesLocation({ ...visibleRangesLocation, predictions: !visibleRangesLocation.predictions })}><Settings /></button>
              </div>
            </div>
            <Popover
              target='#predictionLinesLocation'
              visible={visibleLinesLocation.predictions}
              hideOnOutsideClick={true}
              onHiding={() => setVisibleLinesLocation({ ...visibleLinesLocation, predictions: false })}
            >
              <LinePopover>
                <p>알람 기준선 설정</p>
                { deviceinfo.waterQualityPredictions.length > 0 && deviceinfo.waterQualitySensors.filter((sensor) => { return infomation[sensor.waterQualitySensorId].alarmType !== 'OFF' }).length > 0 ?
                  deviceinfo.waterQualityPredictions.map((prediction: any, index: number) => (
                    infomation[prediction.waterQualityPredictionId].alarmType !== 'OFF' &&
                      <label key={index}>
                        <input
                          type='checkbox'
                          id={`${prediction.waterQualityPredictionId}Location`}
                          checked={linesLocation.predictions === prediction.name}
                          onChange={(event) => setLinesLocation({ ...linesLocation, predictions: event.target.checked ? prediction.name : null })}
                        />
                        <label htmlFor={`${prediction.waterQualityPredictionId}Location`} />
                        { prediction.name }
                      </label>
                    )
                  ) : (
                    <label>설정 없음</label>
                  )
                }
              </LinePopover>
            </Popover>
            <Popover
              target='#predictionRangesLocation'
              visible={visibleRangesLocation.predictions}
              hideOnOutsideClick={true}
              onHiding={() => {
                setVisibleRangesLocation({ ...visibleRangesLocation, predictions: false });
                setRangeLocationPredictions(rangesLocationPredictions);
                setMessageLocation(false);
              }}
            >
              <RangesPopover>
                <p>데이터 범위 설정</p>
                <div>
                  { deviceinfo.waterQualityPredictions.length > 0 && deviceinfo.waterQualityPredictions.map((prediction: any, index: number) => (
                    range &&
                    <div key={index}>
                      <Label>{ prediction.name }</Label>
                      
                      <div>
                        <TextSwitch $text={ rangeLocationPredictions[prediction.waterQualityPredictionId].manual ? '수동' : '자동' }>
                          <label>
                            <input
                              type='checkbox'
                              checked={rangeLocationPredictions[prediction.waterQualityPredictionId].manual}
                              onChange={(event) => setRangeLocationPredictions({
                                ...rangeLocationPredictions,
                                [prediction.waterQualityPredictionId]: {
                                  ...rangeLocationPredictions[prediction.waterQualityPredictionId],
                                  manual: event.target.checked
                                }
                              })}
                            />
                          </label>
                        </TextSwitch>
                        <TextFields
                          type='number'
                          disabled={ rangeLocationPredictions[prediction.waterQualityPredictionId].manual ? false : true }
                          value={ rangeLocationPredictions[prediction.waterQualityPredictionId].startValue }
                          onChange={(event) => setRangeLocationPredictions({
                            ...rangeLocationPredictions,
                            [prediction.waterQualityPredictionId]: {
                              ...rangeLocationPredictions[prediction.waterQualityPredictionId],
                              startValue: Number(event.target.value)
                            }
                          })}
                        />
                        <p>~</p>
                        <TextFields
                          type='number'
                          disabled={ rangeLocationPredictions[prediction.waterQualityPredictionId].manual ? false : true }
                          value={ rangeLocationPredictions[prediction.waterQualityPredictionId].endValue }
                          onChange={(event) => setRangeLocationPredictions({
                            ...rangeLocationPredictions,
                            [prediction.waterQualityPredictionId]: {
                              ...rangeLocationPredictions[prediction.waterQualityPredictionId],
                              endValue: Number(event.target.value)
                            }
                          })}
                        />
                      </div>
                    </div>
                  ))}
                  { messageLocation &&
                    <FormMessage>입력하신 범위를 다시 확인해 주세요.</FormMessage>
                  }
                  <PrimaryButton type='button' onClick={() => handleRangesLocationPredictions()}>저장</PrimaryButton>
                </div>
              </RangesPopover>
            </Popover>
          </div>

          { deviceinfo && legendLocation.predictions && infomation &&
            <Legend>
              { deviceinfo.waterQualityPredictions.map((prediction: any) => (
                <button
                  key={prediction.waterQualityPredictionId}
                  type='button'
                  style={{ 'opacity': legendLocation.predictions[prediction.waterQualityPredictionId] ? '1' : '0.5' }}
                  onClick={() => {
                    if ( legendLocation.predictions[prediction.waterQualityPredictionId] && Object.values(legendLocation.predictions).filter((bool) => {return bool === true}).length < 2 ) return;
                    setLegendLocation({ ...legendLocation, predictions: { ...legendLocation.predictions, [prediction.waterQualityPredictionId]: !legendLocation.predictions[prediction.waterQualityPredictionId] }});
                  }}
                >
                  <div style={{'backgroundColor': `${theme.colors[infomation[prediction.waterQualityPredictionId].color]}`, 'width': '12px', 'height': '12px', 'borderRadius': '2px'}} />
                  { prediction.name }
                </button>
              ))}
            </Legend>
          }
          { infomation && predictionsData &&
            <div style={{paddingTop: 5}}>
              <PredictionsChart
                infomation={infomation}
                datasets={predictionsData}
                lines={linesLocation.predictions}
              />
            </div>
          }
        </Dialog>
      }

      { datasets &&
        <Dialog
          title={datasets.name === 'waterTemperature' ? group.waterTemperature : datasets.name}
          handleClose={closeRecentlySensors}
          chart
        >
          <div style={{ 'width': mobile ? 'calc(100vw - 60px)' : '1000px'}}>
            <div style={{'display': 'flex', 'justifyContent': 'flex-end', 'marginBottom': '10px', 'gap': '5px' }}>
              { datasets.name !== 'waterTemperature' &&
                <button id='linesRecently' onClick={() => setRecentlyVisibleLines(true)}><Line /></button>
              }
              <button id={`${datasets.name}Recently`} onClick={() => setVisibleRecentlyRanges(true)}><Settings /></button>
            </div>
            <Popover
              target='#linesRecently'
              visible={recentlyVisibleLines}
              hideOnOutsideClick={true}
              onHiding={() => setRecentlyVisibleLines(false)}
            >
              <LinePopover>
                <p>알람 기준선 설정</p>
                { datasets.name !== 'waterTemperature' &&
                  datasets.id.length > 0 ?
                  datasets.id.map((id: any) => (
                    info.current[id].alarmType !== 'OFF' &&
                      <label key={`${id}Recently`}>
                        <input type='checkbox' id={`${id}Recently`} checked={recentlyLines === id} onChange={(event) => setRecentlyLines(event.target.checked ? id : null)} />
                        <label htmlFor={`${id}Recently`} />
                        { info.current[id].location }
                      </label>
                    )
                  ) : (
                    <label>설정 없음</label>
                  )
                }
              </LinePopover>
            </Popover>
            <Popover
              target={`#${datasets.name}Recently`}
              visible={visibleRecentlyRanges}
              hideOnOutsideClick={true}
              onHiding={() => {
                setVisibleRecentlyRanges(false);
                setRange(ranges);
                setMessage(false);
              }}
            >
              <RangesPopover>
                <p>데이터 범위 설정</p>
                <div>
                  { range &&
                  <>
                    <div>
                      <div>
                        <TextSwitch $text={ range[datasets.name]?.manual ? '수동' : '자동' }>
                          <label>
                            <input
                              type='checkbox'
                              checked={range[datasets.name]?.manual}
                              onChange={(event) => setRange({
                                ...range,
                                [datasets.name]: {
                                  ...range[datasets.name],
                                  manual: event.target.checked
                                }
                              })}
                            />
                          </label>
                        </TextSwitch>
                        <TextFields
                          type='number'
                          disabled={ range[datasets.name]?.manual ? false : true }
                          value={ range[datasets.name].startValue }
                          onChange={(event) => setRange({
                            ...range,
                            [datasets.name]: {
                              ...range[datasets.name],
                              startValue: Number(event.target.value)
                            }
                          })}
                        />
                        <p>~</p>
                        <TextFields
                          type='number'
                          disabled={ range[datasets.name]?.manual ? false : true }
                          value={ range[datasets.name].endValue }
                          onChange={(event) => setRange({
                            ...range,
                            [datasets.name]: {
                              ...range[datasets.name],
                              endValue: Number(event.target.value)
                            }
                          })}
                        />
                      </div>
                    </div>
                    { message &&
                      <FormMessage>입력하신 범위를 다시 확인해 주세요.</FormMessage>
                    }
                    <PrimaryButton type='button' onClick={() => handleRanges(datasets.name)}>저장</PrimaryButton>
                  </>
                  }
                </div>
              </RangesPopover>
            </Popover>
    
            <Legend>
              { legend && datasets.id.map((id: any, index: number) => (
                <button
                  key={index}
                  type='button'
                  style={{ 'opacity': legend[id] ? '1' : '0.5' }}
                  onClick={() => {
                    if ( legend[id] && Object.values(legend).filter((bool) => {return bool === true}).length < 2 ) return;
                    setLegend({ ...legend, [id]: !legend[id] });
                  }}
                >
                  <div style={{'backgroundColor': `${datasets?.color[id]}`, 'width': '12px', 'height': '12px', 'borderRadius': '2px'}} />
                  { info.current[id].location }
                </button>
              ))}
            </Legend>
    
            { datasets &&
              <RecentlySensors
                info={info}
                datasets={datasets}
                recentlyLines={recentlyLines}
              />
            }
          </div>
        </Dialog>
      }

    </Container>
  );
}